import { useContext, useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { toast } from "react-toastify";
import { Navigate, useNavigate } from "react-router";
import { t } from "i18next";

import PageNotFound from "core/components/routing/pages/pageNotFound/pageNotFound";
import LoginRedirect from "core/components/routing/pages/loginRedirect/loginRedirect";
import SessionCheck from "core/components/routing/sessionCheck";
import { AuthContext } from "core/contexts/auth/auth";
import { Alerts as AC2Cases } from "modules/ac2/components/ac2Alerts/alerts";
import { AnalystDash as AC2Dashboard } from "modules/ac2/components/ac2Home/analystDash";
import { Incidents as FlywheelCases } from "modules/flywheel/components/flywheelIncidents/incidents";
import { AnalystDash as FlywheelDashboard } from "modules/flywheel/components/flywheelHome/analystDash";
import ComplianceTable from "modules/ac2/components/ac2Alerts/complianceTable";
import RagDad from "modules/agentMySecurity/components/ragdad/ragdad";
import CTEP from "modules/userInterfaces/components/cyberTechEvalPlatform/ctep";
import AdminPanel from "modules/agentMySecurity/components/admin/adminPanel";
import AdminApiKey from "modules/agentMySecurity/components/admin/dashboard/apiKeys/apiKeys";
import AdminGroups from "modules/agentMySecurity/components/admin/dashboard/groups/groups";
import AdminInvites from "modules/agentMySecurity/components/admin/dashboard/invites/invites";
import AdminProviders from "modules/agentMySecurity/components/admin/dashboard/iaus/iaus";
import AdminReports from "modules/agentMySecurity/components/admin/dashboard/reports/reports";
import DocsViewer from "modules/agentMySecurity/components/docs/DocsViewer";
import Chat from "modules/chat/components/chat";
import SharedConversation from "modules/chat/components/share/sharedConversation";
import CommingSoon from "./pages/commingSoon/commingSoon";
import SiteRedirect from "./pages/siteRedirect/siteRedirect";
import Logout from "./pages/logout/logout";
import { getProvidersFromBackend } from "modules/chat/services/chatService";
import { useAppDispatch } from "core/store/reduxStore";
import { setProviders } from "modules/chat/store/conversationList";
import ChatHistoryFull from "modules/chat/components/history/chatHistoryFull";
import FavoriteModels from "./pages/sidebarExpandedArea/favorites";
import AdminUsers from "modules/agentMySecurity/components/admin/dashboard/users/users";
import AdminFeedback from "modules/agentMySecurity/components/admin/dashboard/iaus/feedback/feedback";
import AdminDashboard from "modules/agentMySecurity/components/admin/home";
import AdminIaus from "modules/agentMySecurity/components/admin/dashboard/iaus/iaus";
import AdminConversations from "modules/agentMySecurity/components/admin/dashboard/conversations/conversations";
import ExpandedSideBarArea from "./pages/sidebarExpandedArea/home";
import CategoryAlertsTable from "modules/ac2/components/ac2Alerts/categoryAlertsTable";
import ChatBotButton from "modules/ac2/components/ac2Alerts/chatBot";
import { ChatbotProvider } from "modules/ac2/components/ac2Alerts/chatBotContext";
import SearchAgentSuite from "modules/ac2/components/ac2Alerts/agenticChat";
import { ChatProvider } from "modules/ac2/store/chatContext";
import { AlertsSideModal } from "modules/ac2/components/ac2Alerts/alertsSideModal";
import SimilarAlertsTable from "modules/flywheel/components/flywheelIncidents/searchSimilar/searchSimilarIncidents";
import Pentest from "modules/pentest/Pentest";
import Ctem from "modules/ctem/Ctem";
import SentinelPolicyGenerator from "modules/ac2/components/multiCloudPolicy/policyGenerator";
import SidebarLayout from "modules/ac2/components/multiCloudPolicy/newDesignPolicy";
import CstIasp from "modules/cstIasp/CstIasp";
import PentestAutomation from "modules/pentestAutomation/PentestAutomation";
import { EaasAccelerator } from "modules/EaasAccelerator/components/eaasAccelerator";
import MultiCloudAccelerator from "modules/MultiCloudAccelearator/components/multicloud-accelarator";
import LandingHero from "modules/ThreatModelAccelarator/components/LandingHero";
import ModelingProgress from "modules/ThreatModelAccelarator/components/ModelingProgress";
import ThreatModelDashboard from "modules/ThreatModelAccelarator/components/ThreatModelDashboard";

const AppRoutes = () => {
  const navigate = useNavigate();
  // Dismiss all open toasts before opening new route
  toast.dismiss();

  // Clear waiting queue when working with limit
  toast.clearWaitingQueue();

  const { initializeAuth, isAuthInitialized } = useContext(AuthContext);
  
  // We're still keeping access to these for SessionCheck components
  const {
    setAuthToken,
    setAFUser,
    getAMSUser,
    amsBearerToken,
    setIsAvailableAuthUser,
    checkAuthToken,
  } = useContext(AuthContext);

  const dispatch = useAppDispatch();

  const checkUser = async () => {
    console.log("checkUser called, will call getAMSUser");
    // TODO we need one authentication for both IAM and AC2/SOC AI not 2
    // fetch the Agent My Security authentication
    checkAuthToken().then((info) => {
      if (!info) {
        console.log(`check token 4`);
        navigate("/console/login");
      } else {
        getAMSUser();

        setIsAvailableAuthUser(true);

        /**
         * Fetch the token, if getUser was successful, the cookie is available
         */
        const fetchTokenWithCookie = async () => {
          try {
            const token = await setAuthToken();
            fetchNamesFromBackend(token);
          } catch (error) {
            console.error("Error fetching token:", error);
          }
        };

        /**
         * Provider names are needed for the dropdown
         */
        const fetchNamesFromBackend = async (amsBearerToken) => {
          try {
            let names = await getProvidersFromBackend(amsBearerToken);
            
            // console.log("Provider names fetched:", names);

            dispatch(setProviders({ providers: names }));
          } catch (error) {
            console.error("Error fetching provider names:", error);
          }
        };

        // Call the function when the component mounts
        fetchTokenWithCookie();
      }
    });

    // Get the Ac2/SOC AI shared authentication
    

    setIsAvailableAuthUser(true);
  };

  // Application Entry - initialize auth once at app startup
  useEffect(() => {
    if (!isAuthInitialized) {
      console.log('Initializing authentication from routes component');
      initializeAuth();
    }
  }, [isAuthInitialized]);
  
  // Skip the amsBearerToken dependency - auth is now managed centrally

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/console/login" replace />} />

      <Route
        path="/console/login"
        element={<LoginRedirect product={"AMS"} />}
      />

      <Route
        path="/console/login/callback"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<FlywheelDashboard />}
          />
        }
      />

      <Route
        path="/console"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<ExpandedSideBarArea isLandingPage={true} />}
            title={"Models"}
          />
        }
      />

      <Route
        path="/console/redirect/:url"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={SiteRedirect}
            title={""}
          />
        }
      />

      <Route path="/logout" element={<Logout />} />


      <Route
        path="/console/cr/flywheel/home"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<FlywheelDashboard />}
            title={t("SIDEBAR.FLYWHEEL_HOME_LONG") || ""}
          />
        }
      />

      <Route
        path="/console/cr/flywheel/incidents"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<FlywheelCases />}
            title={t("SIDEBAR.FLYWHEEL_INCIDENTS_LONG") || ""}
          />
        }
      />

      <Route
        path="/console/cr/flywheel/incidents/search"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<SimilarAlertsTable />}
            title={t("SIDEBAR.FLYWHEEL_INCIDENTS_LONG") || ""}
          />
        }
      />

      <Route
        path="/console/share/:share_uuid"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={SharedConversation}
            title={""}
          />
        }
      />

      <Route
        path="/console/cp/ac2/home"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<AC2Dashboard />}
            title={t("SIDEBAR.AC2_HOME_LONG") || ""}
          />
        }
      />
 <Route
        path="/console/mcp-accelerator"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<MultiCloudAccelerator />}
            title={t("SIDEBAR.MULTICLOUD_ACCELERATOR") || ""}
          />
        }
      />
      <Route
        path="/console/eaas"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<EaasAccelerator />}
            title={t("SIDEBAR.EAAS_ACCELERATOR") || ""}
          />
        }
      />
      <Route
        path="/console/threat-accelerator"
         element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<LandingHero />}
            title={t("SIDEBAR.THREAT_ACCELERATOR") || "Threat Accelerator"}
          />
        }
      />
      <Route
        path="/console/threat-accelerator/processing"
        
         element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<ModelingProgress />}
            title={t("SIDEBAR.THREAT_ACCELERATOR_PROCESSING") || "Threat Accelerator Processing"}
          />
        }
      />
       <Route
        path="/console/threat-accelerator/:modelId"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<ThreatModelDashboard />}
            title={t("SIDEBAR.THREAT_ACCELERATOR_MODEL") || "Threat Accelerator Model"}
          />
        }
      />
      <Route
        path="/console/cp/ac2/cases"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={
              <ChatbotProvider>
                <AlertsSideModal />
                <AC2Cases />
                <ChatProvider>
                  <SearchAgentSuite />
                </ChatProvider>
                <ChatBotButton />
              </ChatbotProvider>
            }
            title={t("Accenture Continuous Compliance Cases") || "AC2 Cases"}
          />
        }
      />

      <Route
        path="/console/cp/ac2/frameworks"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={
              <ChatbotProvider>
                <AlertsSideModal />
                <ComplianceTable />
                <ChatProvider>
                  <SearchAgentSuite />
                </ChatProvider>
                <ChatBotButton />
              </ChatbotProvider>
            }
            title={
              t("Accenture Continuous Compliance Frameworks") ||
              "AC2 Frameworks"
            }
          />
        }
      />

      <Route
        path="/console/cp/ac2/framework"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={
              <ChatbotProvider>
                <AlertsSideModal />
                <CategoryAlertsTable />
                <ChatProvider>
                  <SearchAgentSuite />
                </ChatProvider>
                <ChatBotButton />
              </ChatbotProvider>
            }
            title={t("Accenture Continuous Compliance Cases") || "AC2 Cases"}
          />
        }
      />

      <Route
        path="/console/secai/docs/main"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<DocsViewer target={"main"} />}
            title={t("SIDEBAR.AMS_DOCS_MAIN") || ""}
          />
        }
      />
      <Route
        path="/console/secai/docs/demo"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<DocsViewer target={"demo"} />}
            title={t("SIDEBAR.AMS_DOCS_MAIN") || ""}
          />
        }
      />
      <Route
        path="/console/secai/docs/general"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<DocsViewer target={"general"} />}
            title={t("SIDEBAR.AMS_DOCS_MAIN") || ""}
          />
        }
      />
      <Route
        path="/console/chat/history"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={ChatHistoryFull}
            title={t("") || ""}
          />
        }
      />

      <Route
        path="/console/chat"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={Chat}
            title={t("") || ""}
          />
        }
      />

      <Route
        path="/console/chat/:provider"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={Chat}
            title={t("") || ""}
          />
        }
      />

      <Route
        path="/console/cstIasp/*"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<CstIasp />}
            title={"cstIasp"}
          />
        }
      />

      <Route
        path="/console/pentestAutomation/*"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<PentestAutomation />}
            title={"pentestAutomation"}
          />
        }
      />

      <Route
        path="/console/soon"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<CommingSoon />}
            title="Coming Soon"
          />
        }
      />
      <Route
        path="/soon"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<CommingSoon />}
            title="Coming Soon"
          />
        }
      />
      <Route
        path="/soon"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<CommingSoon />}
            title="Coming Soon"
          />
        }
      />
      <Route
        path="/console/favorites"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<FavoriteModels />}
            title={"Favorite Models"}
          />
        }
      />
      <Route
        path="/capabilities/:name"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<ExpandedSideBarArea />}
            title={"models"}
          />
        }
      />
      <Route
        path="/console/secai/ragdad"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<RagDad />}
            title={t("SIDEBAR.AMS_RAGDAD_LONG") || ""}
          />
        }
      />

      <Route
        path="/console/secai/ctep"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<CTEP />}
            title={""}
          />
        }
      />

      <Route
        path="/console/secai/docs/main"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<RagDad />}
            title={t("SIDEBAR.AMS_DOCS_MAIN") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminDashboard />} />}
            title={t("SIDEBAR.AMS_ADMIN") || ""}
          />
        }
      />
      <Route
        path="/console/secai/admin/home"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminDashboard />} />}
            title={t("SIDEBAR.AMS_HOME") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin/iaus"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminIaus />} />}
            title={t("SIDEBAR.AMS_IAUS") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin/feedback"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminFeedback />} />}
            title={t("SIDEBAR.AMS_FEEDBACK") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin/users"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminUsers />} />}
            title={t("SIDEBAR.AMS_USERS") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin/apikey"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminApiKey />} />}
            title={t("SIDEBAR.AMS_APIKEY") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin/groups"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminGroups />} />}
            title={t("SIDEBAR.AMS_GROUPS") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin/invite"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminInvites />} />}
            title={t("SIDEBAR.AMS_INVITE") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin/conversations"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={
              <AdminPanel optionalSubComponent={<AdminConversations />} />
            }
            title={t("SIDEBAR.AMS_CONVERSATIONS") || ""}
          />
        }
      />

      <Route
        path="/console/secai/admin/reports"
        element={
          <SessionCheck
            requiredPermissions={["Platform Admin RW", "Platform Admin R"]}
            Component={<AdminPanel optionalSubComponent={<AdminReports />} />}
            title={t("SIDEBAR.AMS_REPORTS") || ""}
          />
        }
      />

      <Route
        path="/console/cr/pentest"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<Pentest/>}
            title={t("SIDEBAR.AMS_REPORTS") || ""}
          />
        }
      />
      <Route
        path="/console/cr/ctem"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<Ctem/>}
            title={t("SIDEBAR.CTEM_REPORTS") || "CTEM Resilience Automation"}
          />
        }
      />

      <Route
        path="/console/cr/ctem"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<Ctem/>}
            title={t("SIDEBAR.CTEM_REPORTS") || "CTEM Resilience Automation"}
          />
        }
      />


      <Route
        path="/console/cr/ctem"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<Ctem/>}
            title={t("SIDEBAR.CTEM_REPORTS") || "CTEM Resilience Automation"}
          />
        }
      />


      <Route
        path="*"
        element={
          <SessionCheck requiredPermissions={[]} Component={<PageNotFound />} />
        }
      />

      {/* 
      <Route
        path="/login/ac2flywheel"
        element={<LoginRedirect product={"AC2FLYWHEEL"} />}
      />

      <Route
        path="/saml/login"
        element={<LoginRedirect product={"AC2FLYWHEEL"} />}
      /> 
      */}

      {/* <Route
        path="/"
        element={
          <SessionCheck
            requiredPermissions={[]}
            Component={<FlywheelDashboard />}
            title={t("SIDEBAR.AC2_HOME_LONG") || ""}
          />
        }
      /> */}
    </Routes>
  );
};

export default AppRoutes;
